import React, { useEffect, useState } from "react";
import { displayToast } from "../utils/displayToast";
import { useErrorHandler } from "../utils/ErrorHandler";
import {
  activateDepartment,
  createDepartment,
  deactivateDepartment,
  deleteDepartment,
  fetchDepartments,
  updateDepartment,
} from "../apis/department";
import "../styles/components/PageStyles.css";
import "../styles/components/Department.css";

const Department = () => {
  const { handleErrorResponse } = useErrorHandler();
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [viewingDepartment, setViewingDepartment] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [form, setForm] = useState({ name: "", code: "" });

  const loadDepartments = async () => {
    try {
      setIsLoading(true);
      const response = await fetchDepartments();
      if (response?.description) {
        handleErrorResponse(response.description);
      } else {
        setDepartments(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      displayToast("Failed to load departments: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredDepartments = departments.filter((department) => {
    const query = searchValue.trim().toLowerCase();
    return !query || [department.name, department.code, department.status]
      .some((value) => String(value || "").toLowerCase().includes(query));
  });

  const openAddForm = () => {
    setCurrentDepartment(null);
    setForm({ name: "", code: "" });
    setShowForm(true);
  };

  const openEditForm = (department) => {
    setCurrentDepartment(department);
    setForm({ name: department.name || "", code: department.code || "" });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setCurrentDepartment(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      displayToast("Department name and code are required", "error");
      return;
    }

    try {
      setIsLoading(true);
      const response = currentDepartment
        ? await updateDepartment(form, currentDepartment.id)
        : await createDepartment(form);
      if (response?.description) {
        handleErrorResponse(response.description);
        return;
      }
      displayToast(
        `Department ${currentDepartment ? "updated" : "created"} successfully!`,
        "success"
      );
      closeForm();
      await loadDepartments();
    } catch (error) {
      displayToast("Failed to save department: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (department) => {
    const isActive = String(department.status).toLowerCase() === "active";
    try {
      setIsLoading(true);
      const response = isActive
        ? await deactivateDepartment(department.id)
        : await activateDepartment(department.id);
      if (response?.description) {
        handleErrorResponse(response.description);
        return;
      }
      displayToast(`Department ${isActive ? "deactivated" : "activated"}!`, "success");
      await loadDepartments();
    } catch (error) {
      displayToast("Failed to update department status: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentDepartment) return;
    try {
      setIsLoading(true);
      const response = await deleteDepartment(currentDepartment.id);
      if (response?.description) {
        handleErrorResponse(response.description);
        return;
      }
      displayToast("Department deleted successfully!", "success");
      setShowDeleteConfirm(false);
      setCurrentDepartment(null);
      await loadDepartments();
    } catch (error) {
      displayToast("Failed to delete department: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const statusClass = (status) =>
    String(status).toLowerCase() === "active"
      ? "mfg-status-active"
      : "mfg-status-inactive";

  return (
    <div className="mfg-page-container">
      <div className="mfg-page-header">
        <div className="mfg-page-title-section">
          <h2 className="mfg-page-title"><span className="mfg-page-title-icon">🏢</span>Departments</h2>
          <p className="mfg-page-subtitle">Manage organisation departments</p>
        </div>
        <button className="mfg-btn mfg-btn-primary" onClick={openAddForm}>
          <span className="mfg-btn-icon">➕</span> Add Department
        </button>
      </div>

      <div className="mfg-card">
        <div className="mfg-card-header">
          <h3 className="mfg-card-title"><span className="mfg-card-title-icon">📋</span>Department List</h3>
        </div>
        <div className="mfg-filters">
          <div className="mfg-search-box department-search-box">
            <input
              className="mfg-search-input"
              placeholder="Search by name, code or status..."
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </div>
        </div>

        {isLoading && departments.length === 0 ? (
          <div className="mfg-loading-state"><span className="loader"></span> Loading departments...</div>
        ) : filteredDepartments.length ? (
          <div className="mfg-table-wrapper">
            <table className="mfg-table">
              <thead><tr><th>Department Name</th><th>Code</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredDepartments.map((department) => {
                  const isActive = String(department.status).toLowerCase() === "active";
                  return (
                    <tr key={department.id}>
                      <td><strong>{department.name}</strong></td>
                      <td>{department.code}</td>
                      <td><span className={`mfg-status-badge ${statusClass(department.status)}`}>{department.status || "N/A"}</span></td>
                      <td><div className="mfg-table-actions department-actions">
                        <button className="mfg-btn mfg-btn-outline mfg-btn-sm" onClick={() => setViewingDepartment(department)}>👁️ <span className="mfg-btn-text">View</span></button>
                        <button className="mfg-btn mfg-btn-warning mfg-btn-sm" onClick={() => openEditForm(department)}>✏️ <span className="mfg-btn-text">Edit</span></button>
                        <button className={`mfg-btn mfg-btn-sm ${isActive ? "mfg-btn-secondary" : "mfg-btn-success"}`} onClick={() => handleStatusChange(department)} disabled={isLoading}>{isActive ? "⏸️ Deactivate" : "▶️ Activate"}</button>
                        <button className="mfg-btn mfg-btn-danger mfg-btn-sm" onClick={() => { setCurrentDepartment(department); setShowDeleteConfirm(true); }} disabled={isActive} title={isActive ? "Deactivate the department before deleting it" : "Delete department"}>🗑️ <span className="mfg-btn-text">Delete</span></button>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mfg-empty-state"><div className="mfg-empty-icon">🏢</div><h4 className="mfg-empty-title">No Departments Found</h4><p className="mfg-empty-text">Create a department to get started</p></div>
        )}
      </div>

      {showForm && (
        <div className="mfg-modal-overlay" onClick={closeForm}>
          <div className="mfg-modal" onClick={(event) => event.stopPropagation()}>
            <div className="mfg-modal-header"><h3 className="mfg-modal-title"><span>🏢</span>{currentDepartment ? "Edit Department" : "Add Department"}</h3><button className="mfg-modal-close" onClick={closeForm}>✕</button></div>
            <form onSubmit={handleSave}>
              <div className="mfg-modal-body department-form-body">
                <div className="mfg-form-group"><label className="mfg-form-label" htmlFor="department-name">Department Name</label><input id="department-name" className="mfg-form-input" name="name" value={form.name} onChange={handleFormChange} placeholder="e.g. Production" autoFocus /></div>
                <div className="mfg-form-group"><label className="mfg-form-label" htmlFor="department-code">Department Code</label><input id="department-code" className="mfg-form-input" name="code" value={form.code} onChange={handleFormChange} placeholder="e.g. PROD" /></div>
              </div>
              <div className="mfg-modal-footer"><button type="button" className="mfg-btn mfg-btn-outline" onClick={closeForm}>Cancel</button><button type="submit" className="mfg-btn mfg-btn-primary" disabled={isLoading}>{isLoading ? "Saving..." : currentDepartment ? "Update Department" : "Save Department"}</button></div>
            </form>
          </div>
        </div>
      )}

      {viewingDepartment && (
        <div className="mfg-modal-overlay" onClick={() => setViewingDepartment(null)}>
          <div className="mfg-modal" onClick={(event) => event.stopPropagation()}>
            <div className="mfg-modal-header"><h3 className="mfg-modal-title"><span>🏢</span>Department Details</h3><button className="mfg-modal-close" onClick={() => setViewingDepartment(null)}>✕</button></div>
            <div className="mfg-modal-body"><div className="mfg-view-grid"><div className="mfg-view-item"><div className="mfg-view-label">Department Name</div><div className="mfg-view-value">{viewingDepartment.name}</div></div><div className="mfg-view-item"><div className="mfg-view-label">Code</div><div className="mfg-view-value">{viewingDepartment.code}</div></div><div className="mfg-view-item"><div className="mfg-view-label">Status</div><div className="mfg-view-value"><span className={`mfg-status-badge ${statusClass(viewingDepartment.status)}`}>{viewingDepartment.status || "N/A"}</span></div></div></div></div>
            <div className="mfg-modal-footer"><button className="mfg-btn mfg-btn-outline" onClick={() => setViewingDepartment(null)}>Close</button><button className="mfg-btn mfg-btn-warning" onClick={() => { setViewingDepartment(null); openEditForm(viewingDepartment); }}>✏️ Edit Department</button></div>
          </div>
        </div>
      )}

      {showDeleteConfirm && currentDepartment && (
        <div className="mfg-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="mfg-modal" onClick={(event) => event.stopPropagation()}>
            <div className="mfg-modal-header"><h3 className="mfg-modal-title"><span>⚠️</span>Confirm Delete</h3><button className="mfg-modal-close" onClick={() => setShowDeleteConfirm(false)}>✕</button></div>
            <div className="mfg-modal-body"><p className="mfg-modal-confirm-text">Delete <strong>{currentDepartment.name}</strong>?</p><p className="mfg-modal-danger-text">Only inactive departments can be deleted. Linked departments may also be rejected by the server.</p></div>
            <div className="mfg-modal-footer"><button className="mfg-btn mfg-btn-outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</button><button className="mfg-btn mfg-btn-danger" onClick={handleDelete} disabled={isLoading}>{isLoading ? "Deleting..." : "🗑️ Confirm Delete"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Department;